import { createContext, useContext, useState, useEffect, useCallback } from 'react'

const ThemeContext = createContext()

const ACCENT_COLORS = {
  '#6366f1': { hover: '#4f46e5', light: 'rgba(99, 102, 241, 0.08)', glow: 'rgba(99, 102, 241, 0.18)' },
  '#8b5cf6': { hover: '#7c3aed', light: 'rgba(139, 92, 246, 0.08)', glow: 'rgba(139, 92, 246, 0.18)' },
  '#ec4899': { hover: '#db2777', light: 'rgba(236, 72, 153, 0.08)', glow: 'rgba(236, 72, 153, 0.18)' },
  '#ef4444': { hover: '#dc2626', light: 'rgba(239, 68, 68, 0.08)', glow: 'rgba(239, 68, 68, 0.18)' },
  '#f59e0b': { hover: '#d97706', light: 'rgba(245, 158, 11, 0.08)', glow: 'rgba(245, 158, 11, 0.18)' },
  '#22c55e': { hover: '#16a34a', light: 'rgba(34, 197, 94, 0.08)', glow: 'rgba(34, 197, 94, 0.18)' },
  '#3b82f6': { hover: '#2563eb', light: 'rgba(59, 130, 246, 0.08)', glow: 'rgba(59, 130, 246, 0.18)' },
  '#14b8a6': { hover: '#0d9488', light: 'rgba(20, 184, 166, 0.08)', glow: 'rgba(20, 184, 166, 0.18)' },
}

const DENSITY_VALUES = {
  compact: { spacing: '0.5rem', padding: '0.5rem 0.75rem', gap: '0.5rem' },
  comfortable: { spacing: '0.75rem', padding: '0.75rem 1rem', gap: '0.75rem' },
  spacious: { spacing: '1rem', padding: '1rem 1.25rem', gap: '1rem' },
}

function getSystemTheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('synx-theme') || 'dark')
  const [accentColor, setAccentColor] = useState(() => localStorage.getItem('synx-accent') || '#6366f1')
  const [density, setDensity] = useState(() => localStorage.getItem('synx-density') || 'comfortable')

  const resolvedTheme = theme === 'system' ? getSystemTheme() : theme

  // Apply theme class to document
  const applyTheme = useCallback(() => {
    const root = document.documentElement
    root.setAttribute('data-theme', resolvedTheme)
  }, [resolvedTheme])

  // Apply accent color CSS variables
  const applyAccent = useCallback(() => {
    const root = document.documentElement
    const colors = ACCENT_COLORS[accentColor] || ACCENT_COLORS['#6366f1']
    root.style.setProperty('--color-primary', accentColor)
    root.style.setProperty('--color-primary-hover', colors.hover)
    root.style.setProperty('--color-primary-light', colors.light)
    root.style.setProperty('--color-primary-glow', colors.glow)
  }, [accentColor])

  // Apply density CSS variables
  const applyDensity = useCallback(() => {
    const root = document.documentElement
    const values = DENSITY_VALUES[density] || DENSITY_VALUES.comfortable
    root.style.setProperty('--density-spacing', values.spacing)
    root.style.setProperty('--density-padding', values.padding)
    root.style.setProperty('--density-gap', values.gap)
  }, [density])

  // Apply all on mount and when values change
  useEffect(() => { applyTheme() }, [applyTheme])
  useEffect(() => { applyAccent() }, [applyAccent])
  useEffect(() => { applyDensity() }, [applyDensity])

  // Listen for system theme changes when in 'system' mode
  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => applyTheme()
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme, applyTheme])

  // Persist to localStorage
  useEffect(() => { localStorage.setItem('synx-theme', theme) }, [theme])
  useEffect(() => { localStorage.setItem('synx-accent', accentColor) }, [accentColor])
  useEffect(() => { localStorage.setItem('synx-density', density) }, [density])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, accentColor, setAccentColor, density, setDensity, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
